import { useState } from "react"
import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { gettext } from "@multimediallc/web-utils"
import { useCrossFrameOutsideClick } from "../../../hooks/useCrossFrameOutsideClick"
import { Sizes, Weights } from "../../../interfaces/sizing"
import { mergeClasses } from "../../../utils/css"
import { Button, ButtonSize, Typography } from "../../common"
import { CheckmarkSlim } from "../../common/atoms/Icons/Others"
import "./AppDirToggle.scss"

const i18n = {
    modernDesign: gettext("Modern (New Design)"),
    legacyDesign: gettext("Legacy Design"),
}

type PreviewStatus = "v3" | "legacy"

export interface AppDirToggleProps {
    value: PreviewStatus
    iframeLink: string
    onChange: (value: PreviewStatus) => void
    onShowcaseClick?: () => void
    showShowcase?: boolean
}

interface ToogleOption {
    label: string
    value: PreviewStatus
}

const toggleOptions: ToogleOption[] = [
    {
        label: i18n.modernDesign,
        value: "v3",
    },
    {
        label: i18n.legacyDesign,
        value: "legacy",
    },
]

export const AppDirToggle = ({
    iframeLink,
    value: toggleValue,
    onChange,
    onShowcaseClick,
    showShowcase = false,
}: AppDirToggleProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [position, setPosition] = useState<"top" | "bottom">("top")
    const ref = useCrossFrameOutsideClick(
        () => setIsOpen(false),
        [],
        [iframeLink],
    )

    const toggleMenu = () => {
        if (ref.current) {
            const toggleElement = ref.current
            const rect = toggleElement.getBoundingClientRect()
            const spaceAbove = rect.top
            const spaceBelow = window.innerHeight - rect.bottom
            if (spaceAbove < 100 && spaceBelow > 100) {
                setPosition("bottom")
            } else {
                setPosition("top")
            }
        }
        setIsOpen((prev) => !prev)
    }

    const handleSelectValue =
        (value: PreviewStatus) =>
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            ev.stopPropagation()
            onChange(value)
            setIsOpen(false)
        }

    return (
        <div className="AppDirToggle" ref={ref} data-test="v3-toggle-input">
            <div
                className="AppDirToggle__title"
                onClick={toggleMenu}
                data-test="v3-toggle-area"
            >
                <Typography
                    component="span"
                    size={Sizes.xspx}
                    weight={Weights.normal}
                    className="AppDirToggle__title__label"
                >
                    <Trans>New</Trans>
                </Typography>
                <Typography
                    component="h6"
                    size={Sizes.xspx}
                    weight={Weights.medium}
                    className="AppDirToggle__title__name"
                >
                    <Trans>App Directory Preference</Trans>
                </Typography>
            </div>
            {isOpen && (
                <ul
                    className={mergeClasses(
                        "AppDirToggle__list",
                        position === "bottom"
                            ? "AppDirToggle__list--bottom"
                            : "",
                    )}
                    data-test="v3-toggle-list"
                >
                    {toggleOptions.map(({ label, value }) => (
                        <Typography
                            component="li"
                            size={Sizes.smpx}
                            weight={Weights.normal}
                            className={mergeClasses(
                                "AppDirToggle__list__option",
                                toggleValue === value
                                    ? "AppDirToggle__list__option--active"
                                    : "",
                            )}
                            key={value}
                            onClick={handleSelectValue(value)}
                            data-test="v3-toggle-option"
                        >
                            {label}
                            {toggleValue === value && (
                                <CheckmarkSlim
                                    className="AppDirToggle__list__option__checkmark"
                                    data-test="v3-toggle-option-checkmark"
                                />
                            )}
                        </Typography>
                    ))}
                </ul>
            )}
            {showShowcase && (
                <div className="AppDirToggle__showcase">
                    <Typography
                        component="h6"
                        size={Sizes.xspx}
                        weight={Weights.bold}
                        className="AppDirToggle__showcase__title"
                    >
                        <Trans>Adjust between legacy and new designs</Trans>
                    </Typography>
                    <Typography
                        component="p"
                        size={Sizes.xspx}
                        weight={Weights.normal}
                        className="AppDirToggle__showcase__description"
                    >
                        <Trans>
                            You can always switch the view between the new and
                            legacy designs by clicking here.
                        </Trans>
                    </Typography>
                    <Button
                        text={t`Got it`}
                        size={ButtonSize.None}
                        onClick={onShowcaseClick}
                        data-test="tooltip-btn"
                        className="AppDirToggle__showcase__btn"
                    />
                </div>
            )}
        </div>
    )
}
